/**
 * Bewerbungsformular Stufe 1 — Öffentliche React-Route
 * =====================================================
 *
 * URL: /bewerbung/:tenantSlug/:listingSlug (öffentlich, kein Login)
 *
 * GDPR "Stufen-Prinzip":
 *   - Stufe 1 (dieses Formular): Kontakt + Einzugswunsch + Bedarfe +
 *     wirtschaftliche Grund-Angabe (KEINE Nachweise, KEINE Ausweise,
 *     KEIN exakter Einkommensbetrag)
 *   - Stufe 2: Erweiterte Angaben (per Magic-Link)
 *   - Stufe 3: Nachweise / Dokumente (per Magic-Link)
 *
 * Kann sowohl standalone als auch im iFrame verwendet werden — Layout
 * paßt sich automatisch (URL-Query ?embed=1 blendet Header/Footer aus).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle, Warning, Envelope, Phone as PhoneIcon, House, User } from "@phosphor-icons/react";

const STEPS = [
  { id: 1, label: "Kontakt", icon: User },
  { id: 2, label: "Einzug", icon: House },
  { id: 3, label: "Bedarfe", icon: Envelope },
  { id: 4, label: "Wirtschaftliches", icon: PhoneIcon },
];

export default function BewerbungForm() {
  const { tenantSlug, listingSlug } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get("embed") === "1";

  const [tenant, setTenant] = useState(null);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    preferred_contact: "email",
    consent_data_processing: false,
    // 2026-02-24 · iter172 — Interessentenstamm: KEIN Default-Häkchen (Art. 7 DSGVO)
    interessentenstamm_consent: false,
    interessentenstamm_consent_version: "1.0",
    desired_move_in: "", contract_duration: "unbefristet", move_reason: "",
    persons_count: 1, pets: false, pets_details: "", smoking: false,
    usage: "main_residence", residential_only: true,
    income_source: "employment", employment_type: "permanent", income_bracket: "",
    note: "",
    // Honeypot – muss leer bleiben
    website: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Öffentliches Endpoint – ohne Login
        const { data } = await api.get(`/embed/${tenantSlug}/listings/${listingSlug}`);
        if (cancelled) return;
        // Backend liefert reines Listing-Objekt bei /embed → nutze /marketing/public
        setListing(data);
        // Tenant-Meta aus Listen-Endpoint holen
        const { data: listData } = await api.get(`/embed/${tenantSlug}/listings`).catch(() => ({ data: null }));
        if (listData?.tenant) setTenant(listData.tenant);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Inserat nicht gefunden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantSlug, listingSlug]);

  const canProceed = useMemo(() => {
    if (step === 1) {
      return form.first_name.trim() && form.last_name.trim()
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
        && form.consent_data_processing;
    }
    if (step === 2) return true;
    if (step === 3) return form.persons_count >= 1;
    return true;
  }, [step, form]);

  const handleSubmit = async () => {
    if (!form.consent_data_processing) return;
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        preferred_contact: form.preferred_contact,
        desired_move_in: form.desired_move_in || null,
        persons_count: Number(form.persons_count) || 1,
        usage: form.usage,
        residential_only: form.residential_only,
        pets: form.pets,
        pets_details: form.pets ? (form.pets_details || null) : null,
        income_source: form.income_source,
        employment_type: form.employment_type,
        income_bracket: form.income_bracket || null,
        note: [
          form.contract_duration ? `Vertragsdauer: ${form.contract_duration}` : "",
          form.move_reason ? `Umzugsgrund: ${form.move_reason}` : "",
          form.smoking ? "Raucher: ja" : "",
          form.note ? `\nAnmerkung: ${form.note}` : "",
        ].filter(Boolean).join(" · ") || null,
        consent_data_processing: true,
        interessentenstamm_consent: !!form.interessentenstamm_consent,
        interessentenstamm_consent_version: form.interessentenstamm_consent_version || "1.0",
        interessentenstamm_consent_text: form.interessentenstamm_consent
          ? "Ja, ich möchte in den Interessentenstamm aufgenommen und über zukünftig passende Immobilienangebote informiert werden. Diese Einwilligung kann ich jederzeit mit Wirkung für die Zukunft widerrufen."
          : null,
        website: form.website, // honeypot
      };
      await api.post(`/embed/${tenantSlug}/listings/${listing.id}/apply`, payload);
      setSubmitted(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Fehler beim Absenden";
      setError(String(msg));
    } finally { setSubmitting(false); }
  };

  const brandColor = tenant?.primary_color || "#0F172A";

  if (loading) {
    return <PageWrapper isEmbed={isEmbed}><div className="text-slate-500 text-sm py-10 text-center">Lade Inserat…</div></PageWrapper>;
  }
  if (error && !listing) {
    return <PageWrapper isEmbed={isEmbed}><ErrorCard message={error} /></PageWrapper>;
  }
  if (submitted) {
    return (
      <PageWrapper isEmbed={isEmbed} tenant={tenant} listing={listing}>
        <div className="text-center py-10 space-y-3" data-testid="bewerbung-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle size={40} className="text-emerald-700" weight="fill" />
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">Vielen Dank für Ihre Bewerbung!</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            Ihre Angaben wurden übermittelt. Der Vermieter meldet sich bei Ihnen
            per {form.preferred_contact === "phone" ? "Telefon" : "E-Mail"}.
            Wenn zur weiteren Prüfung Unterlagen benötigt werden, erhalten Sie
            einen sicheren Link per E-Mail.
          </p>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper isEmbed={isEmbed} tenant={tenant} listing={listing}>
      {/* Stepper */}
      <div className="flex items-center justify-between mb-6" data-testid="bewerbung-stepper">
        {STEPS.map((s, idx) => {
          const active = step === s.id;
          const done = step > s.id;
          return (
            <div key={s.id} className="flex items-center flex-1 last:flex-none">
              <div className={`flex items-center gap-2 ${active || done ? "text-slate-900" : "text-slate-400"}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                    done ? "bg-emerald-600 border-emerald-600 text-white" : (active ? "border-slate-900" : "border-slate-300")
                  }`}
                  style={active ? { borderColor: brandColor, color: brandColor } : {}}
                >
                  {done ? <CheckCircle size={14} weight="fill" /> : s.id}
                </div>
                <span className="text-xs font-medium hidden sm:inline">{s.label}</span>
              </div>
              {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          );
        })}
      </div>

      {/* Steps */}
      {step === 1 && <Step1 form={form} set={set} />}
      {step === 2 && <Step2 form={form} set={set} />}
      {step === 3 && <Step3 form={form} set={set} />}
      {step === 4 && <Step4 form={form} set={set} />}

      {/* Honeypot */}
      <div style={{ position: "absolute", left: -9999, top: -9999 }}>
        <label>Website (nicht ausfüllen)</label>
        <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex="-1" autoComplete="off" />
      </div>

      {/* Footer navigation */}
      <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between gap-2">
        <Button
          variant="outline"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          data-testid="bewerbung-prev"
        >
          Zurück
        </Button>
        <div className="text-[10px] text-slate-400">Schritt {step} von {STEPS.length}</div>
        {step < STEPS.length ? (
          <Button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canProceed}
            style={{ backgroundColor: brandColor }}
            className="text-white hover:opacity-90"
            data-testid="bewerbung-next"
          >
            Weiter
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting || !form.consent_data_processing}
            style={{ backgroundColor: brandColor }}
            className="text-white hover:opacity-90"
            data-testid="bewerbung-submit"
          >
            {submitting ? "Wird gesendet…" : "Bewerbung absenden"}
          </Button>
        )}
      </div>

      {error && <div className="mt-3 p-2 rounded bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-center gap-1.5"><Warning size={12} weight="fill" /> {error}</div>}
    </PageWrapper>
  );
}

function PageWrapper({ children, tenant, listing, isEmbed }) {
  const primary = tenant?.primary_color || "#0F172A";
  if (isEmbed) {
    return (
      <div className="min-h-screen bg-white p-4 sm:p-6" data-testid="bewerbung-embed">
        <div className="max-w-2xl mx-auto">
          {listing && <ListingHeader listing={listing} tenant={tenant} compact />}
          <div className="bg-white rounded-lg p-4">
            {children}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-50" data-testid="bewerbung-page">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.display_name || ""} className="h-10 object-contain" />
          ) : (
            <div className="text-lg font-semibold" style={{ color: primary }}>
              {tenant?.display_name || "Vermietung"}
            </div>
          )}
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {listing && <ListingHeader listing={listing} tenant={tenant} />}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 mt-4 shadow-sm">
          {children}
        </div>
        <div className="text-[10px] text-slate-400 text-center mt-4">
          powered by Domivus · Ihre Angaben werden ausschließlich zur Prüfung dieser Bewerbung
          verwendet. Details siehe Datenschutzerklärung des Vermieters.
        </div>
      </div>
    </div>
  );
}

function ListingHeader({ listing, tenant, compact }) {
  const primary = tenant?.primary_color || "#0F172A";
  return (
    <div className={`rounded-lg overflow-hidden ${compact ? "" : "border border-slate-200 bg-white shadow-sm"}`}>
      {!compact && listing.images?.[0]?.url && (
        <img src={listing.images[0].url} alt={listing.title || ""} className="w-full h-40 sm:h-56 object-cover" />
      )}
      <div className={compact ? "" : "p-4"}>
        <h1 className={`font-semibold text-slate-900 ${compact ? "text-lg" : "text-xl"}`}>
          Bewerbung für: {listing.title || "Wohnung"}
        </h1>
        {listing.address && <div className="text-xs text-slate-500 mt-0.5">{listing.address}</div>}
        <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-2">
          {listing.living_area && <span>{listing.living_area} m²</span>}
          {listing.rooms && <span>· {listing.rooms} Zimmer</span>}
          {listing.rent_cold && <span>· {Number(listing.rent_cold).toFixed(0)} € Kaltmiete</span>}
        </div>
      </div>
      {!compact && (
        <div className="px-4 pb-3 text-xs text-slate-500">
          Bitte füllen Sie die folgenden Angaben aus. Wir bitten in dieser Phase
          <b> nur um allgemeine Angaben</b> — keine Ausweise, keine Nachweise, kein exaktes Einkommen.
        </div>
      )}
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 text-center">
      {message}
    </div>
  );
}

// ============================================================================
// Steps
// ============================================================================
function Step1({ form, set }) {
  return (
    <div className="space-y-4" data-testid="bewerbung-step-1">
      <h2 className="text-lg font-semibold">Ihre Kontaktdaten</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Vorname *</Label>
          <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="bewerbung-first-name" />
        </div>
        <div>
          <Label>Nachname *</Label>
          <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="bewerbung-last-name" />
        </div>
      </div>
      <div>
        <Label>E-Mail *</Label>
        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="bewerbung-email" />
      </div>
      <div>
        <Label>Telefon (optional)</Label>
        <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="bewerbung-phone" />
      </div>
      <div>
        <Label>Kontakt bevorzugt via</Label>
        <div className="flex gap-4 mt-1">
          <label className="text-sm flex items-center gap-1">
            <input type="radio" checked={form.preferred_contact === "email"} onChange={() => set("preferred_contact", "email")} /> E-Mail
          </label>
          <label className="text-sm flex items-center gap-1">
            <input type="radio" checked={form.preferred_contact === "phone"} onChange={() => set("preferred_contact", "phone")} /> Telefon
          </label>
        </div>
      </div>
      <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
        <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-700">
          <input
            type="checkbox"
            checked={form.consent_data_processing}
            onChange={(e) => set("consent_data_processing", e.target.checked)}
            className="mt-0.5"
            data-testid="bewerbung-consent"
          />
          <span>
            Ich willige ein, dass meine hier gemachten Angaben zur Prüfung dieser Wohnungsbewerbung
            verarbeitet werden. In diesem Schritt werden <b>keine Nachweise oder Ausweise</b>
            erhoben. Sollten weitere Angaben notwendig werden, erhalte ich einen sicheren Link
            per E-Mail. Die Angaben werden ausschließlich vom Vermieter/der Verwaltung eingesehen.
            *
          </span>
        </label>
      </div>
      {/* 2026-02-24 · iter172 — Interessentenstamm (freiwilliger, separater Zweck) */}
      <div className="rounded-md bg-emerald-50/60 border border-emerald-200 p-3">
        <div className="text-xs font-semibold text-emerald-900 mb-1">
          Möchten Sie auch zukünftig über passende Immobilienangebote informiert werden und keine
          neue Wohnung verpassen?
        </div>
        <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-700">
          <input
            type="checkbox"
            checked={!!form.interessentenstamm_consent}
            onChange={(e) => set("interessentenstamm_consent", e.target.checked)}
            className="mt-0.5"
            data-testid="bewerbung-consent-pool"
          />
          <span>
            Ja, ich möchte in den <b>Interessentenstamm</b> aufgenommen und über zukünftig
            passende Immobilienangebote informiert werden. Diese Einwilligung kann ich jederzeit
            mit Wirkung für die Zukunft widerrufen (z. B. über den Link am Ende jeder Nachricht).
            <br />
            <span className="text-slate-500">
              Diese Angabe ist freiwillig und keine Voraussetzung für Ihre Bewerbung auf die
              konkrete Wohnung.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

function Step2({ form, set }) {
  return (
    <div className="space-y-4" data-testid="bewerbung-step-2">
      <h2 className="text-lg font-semibold">Einzugswunsch</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Frühester Einzug</Label>
          <Input type="date" value={form.desired_move_in} onChange={(e) => set("desired_move_in", e.target.value)} data-testid="bewerbung-move-in" />
        </div>
        <div>
          <Label>Gewünschte Vertragsdauer</Label>
          <Select value={form.contract_duration} onValueChange={(v) => set("contract_duration", v)}>
            <SelectTrigger data-testid="bewerbung-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unbefristet">Unbefristet</SelectItem>
              <SelectItem value="12m">Ca. 1 Jahr</SelectItem>
              <SelectItem value="24m">Ca. 2 Jahre</SelectItem>
              <SelectItem value="36m">Länger als 2 Jahre</SelectItem>
              <SelectItem value="egal">Ist mir gleich</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Umzugsgrund (optional)</Label>
        <Textarea rows={2} value={form.move_reason} onChange={(e) => set("move_reason", e.target.value)}
                  placeholder="z. B. beruflicher Wechsel, mehr Platz benötigt, Familienzuwachs …"
                  data-testid="bewerbung-move-reason" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Nutzungsart</Label>
          <Select value={form.usage} onValueChange={(v) => set("usage", v)}>
            <SelectTrigger data-testid="bewerbung-usage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="main_residence">Hauptwohnsitz</SelectItem>
              <SelectItem value="secondary_residence">Zweitwohnsitz</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Nur zu Wohnzwecken?</Label>
          <Select value={form.residential_only ? "yes" : "no"} onValueChange={(v) => set("residential_only", v === "yes")}>
            <SelectTrigger data-testid="bewerbung-residential"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Ja – ausschließlich zum Wohnen</SelectItem>
              <SelectItem value="no">Nein – teilweise gewerblich</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function Step3({ form, set }) {
  return (
    <div className="space-y-4" data-testid="bewerbung-step-3">
      <h2 className="text-lg font-semibold">Ihre Bedarfe</h2>
      <div>
        <Label>Anzahl einziehender Personen *</Label>
        <Input
          type="number" min="1" max="20"
          value={form.persons_count}
          onChange={(e) => set("persons_count", Math.max(1, Number(e.target.value) || 1))}
          data-testid="bewerbung-persons"
        />
      </div>
      <div className="rounded-md border border-slate-200 p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.pets} onChange={(e) => set("pets", e.target.checked)} data-testid="bewerbung-pets" />
          Tierhaltung geplant
        </label>
        {form.pets && (
          <Textarea rows={2} value={form.pets_details} onChange={(e) => set("pets_details", e.target.value)}
                    placeholder="Welche Tiere? (z. B. 1 Katze, 1 Hund Rasse …)"
                    data-testid="bewerbung-pets-details" />
        )}
      </div>
      <div className="rounded-md border border-slate-200 p-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.smoking} onChange={(e) => set("smoking", e.target.checked)} data-testid="bewerbung-smoking" />
          In der Wohnung wird geraucht
        </label>
      </div>
    </div>
  );
}

function Step4({ form, set }) {
  const incomeBrackets = [
    { v: "u1500", l: "unter 1.500 €" },
    { v: "1500_2000", l: "1.500 – 2.000 €" },
    { v: "2000_2500", l: "2.000 – 2.500 €" },
    { v: "2500_3000", l: "2.500 – 3.000 €" },
    { v: "3000_4000", l: "3.000 – 4.000 €" },
    { v: "4000_5000", l: "4.000 – 5.000 €" },
    { v: "o5000", l: "über 5.000 €" },
  ];
  return (
    <div className="space-y-4" data-testid="bewerbung-step-4">
      <h2 className="text-lg font-semibold">Wirtschaftliche Grund-Angabe</h2>
      <p className="text-xs text-slate-500 -mt-2">
        Diese Angaben helfen dem Vermieter, die Bewerbung einzuordnen. Der genaue Betrag wird
        <b> nicht</b> abgefragt — nur die Einkommensspanne des Haushalts.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Haupt-Einkommensquelle</Label>
          <Select value={form.income_source} onValueChange={(v) => set("income_source", v)}>
            <SelectTrigger data-testid="bewerbung-income-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employment">Angestellte(r)</SelectItem>
              <SelectItem value="self_employment">Selbstständig</SelectItem>
              <SelectItem value="pension">Rente / Pension</SelectItem>
              <SelectItem value="public_benefits">Öffentliche Leistungen</SelectItem>
              <SelectItem value="alimony_other">Unterhalt / Sonstiges</SelectItem>
              <SelectItem value="multiple">Mehrere Quellen</SelectItem>
              <SelectItem value="other">Andere</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(form.income_source === "employment" || form.income_source === "self_employment" || form.income_source === "multiple") && (
          <div>
            <Label>Beschäftigungsart</Label>
            <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
              <SelectTrigger data-testid="bewerbung-employment-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">Unbefristet</SelectItem>
                <SelectItem value="temporary">Befristet</SelectItem>
                <SelectItem value="probation">In Probezeit</SelectItem>
                <SelectItem value="training">Auszubildende(r) / Studium</SelectItem>
                <SelectItem value="other">Andere</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div>
        <Label>Haushalts-Nettoeinkommen (Spanne)</Label>
        <Select value={form.income_bracket} onValueChange={(v) => set("income_bracket", v)}>
          <SelectTrigger data-testid="bewerbung-income-bracket">
            <SelectValue placeholder="Bitte wählen …" />
          </SelectTrigger>
          <SelectContent>
            {incomeBrackets.map((b) => (
              <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Anmerkung (optional)</Label>
        <Textarea rows={3} value={form.note} onChange={(e) => set("note", e.target.value)}
                  placeholder="Wenn Sie dem Vermieter noch etwas mitteilen möchten …"
                  data-testid="bewerbung-note" />
      </div>
    </div>
  );
}
